import { branchFeatureFlags } from "./branchFeatureFlags";
import { isOfflineTentative, isPhase2Continuation } from "./phaseConstants";

/**
 * Reviewer Application Edit: a Level 1, 2 or 3 reviewer corrects the form
 * fields of an application directly, in place of returning it to the
 * applicant. Mirrors
 * civic-plan-backend/api/reviewer-application-edit/reviewer-application-edit.constants.js;
 * keep the two in step. The backend makes the final decision on every save;
 * these rules only decide how the Edit button is drawn.
 */

export const REVIEWER_EDIT_APPLICATION_ROUTE = "/reviewer-edit-application";

/**
 * The statuses in which each role may edit: only while the application is at
 * the reviewer's own level, and never from Pending Payment onwards. A level
 * is listed only because its own Demand Note screen handles an outdated
 * Demand Note. Level 3's two later statuses (Pending Final Review, Pending
 * Rejection Confirmation) are left out on purpose.
 */
export const EDITABLE_STATUSES_BY_ROLE: Record<string, readonly string[]> = {
  "level 1": ["Pending Level 1 Review"],
  "level 2": ["Pending Level 2 Review"],
  "level 3": ["Pending Level 3 Review"],
};

export const EDIT_LOCK_REASON = {
  TYPE_NOT_ALLOWED: "Editing is not available for this application type.",
  NOT_AT_YOUR_LEVEL: "This application is not at your level.",
  STATUS_CLOSED: "Editing is closed for this application's current status.",
} as const;

type ReviewerEditApplication =
  | { status?: string; rl_application_type?: string; current_phase?: string }
  | Record<string, unknown>
  | null
  | undefined;

/** True when this role is ever offered the Edit button. */
export function canRoleEditApplications(role?: string | null): boolean {
  if (!branchFeatureFlags.ENABLE_REVIEWER_APPLICATION_EDIT) return false;
  return !!EDITABLE_STATUSES_BY_ROLE[String(role || "").trim().toLowerCase()];
}

/**
 * Why the Edit button is disabled for this application, or null when the
 * reviewer may edit it. Only meaningful for a role that
 * canRoleEditApplications() accepts.
 */
export function getReviewerEditLock(
  application: ReviewerEditApplication,
  role?: string | null
): string | null {
  const ownStatuses =
    EDITABLE_STATUSES_BY_ROLE[String(role || "").trim().toLowerCase()] || [];
  const status = String((application as { status?: unknown })?.status || "");

  if (isPhase2Continuation(application) || isOfflineTentative(application)) {
    return EDIT_LOCK_REASON.TYPE_NOT_ALLOWED;
  }

  if (ownStatuses.includes(status)) return null;

  const isAtAnotherLevel = Object.values(EDITABLE_STATUSES_BY_ROLE).some(
    (statuses) => statuses.includes(status)
  );
  return isAtAnotherLevel
    ? EDIT_LOCK_REASON.NOT_AT_YOUR_LEVEL
    : EDIT_LOCK_REASON.STATUS_CLOSED;
}

/**
 * The values a saved Demand Note copies from the application. When the
 * application was edited after the Demand Note was saved, these are dropped
 * from the saved copy so the Demand Note form falls back to the application's
 * current values. The reviewer's own entries (due date, usage type,
 * conditions) are kept. The plot area used for the charges is decided
 * separately by getChargeAreaDefault() below.
 */
export const DEMAND_NOTE_FIELDS_COPIED_FROM_APPLICATION = [
  "applicantName",
  "layoutName",
  "mauja",
  "plotNo",
  "khasaraNo",
  "main_khasara_no",
  "manual_khasara_no",
] as const;

export const OUTDATED_DEMAND_NOTE_NOTICE =
  "This application was edited after the Demand Note was last saved. The applicant details below now show the edited values and the charges are recalculated. Check them and press Save & Preview before forwarding.";

/**
 * Returns the saved Demand Note the form should start from. Unchanged when
 * the Demand Note is up to date; without the application-copied fields when
 * the backend marked it as saved before the last reviewer edit.
 */
export function getDemandNoteFormDefaults<
  T extends { applicationEditedAfterSave?: boolean } & Record<string, any>
>(savedDemandNote: T | undefined): T | undefined {
  if (!savedDemandNote?.applicationEditedAfterSave) return savedDemandNote;

  const defaults: Record<string, any> = { ...savedDemandNote };
  DEMAND_NOTE_FIELDS_COPIED_FROM_APPLICATION.forEach((key) => {
    delete defaults[key];
  });
  return defaults as T;
}

/**
 * The "Area of Plot" the Demand Note form should start its charges from.
 *
 * Normally this is the area saved with the Demand Note, which the reviewer
 * typed. When the application was edited after that save and its plot area
 * is no longer the one the Demand Note was saved with, the edited area is
 * used, so the charges are recalculated for it.
 *
 * The saved area is kept when the Demand Note has no record of the plot
 * area it was saved with, and when the application's area is in square
 * feet, because the charges are worked out in square metres.
 */
export function getChargeAreaDefault(
  savedDemandNote:
    | ({ applicationEditedAfterSave?: boolean } & Record<string, any>)
    | null
    | undefined,
  application: Record<string, any> | null | undefined
): number | string | undefined {
  const savedChargeArea = savedDemandNote?.areaOfPlot;
  if (!savedDemandNote?.applicationEditedAfterSave) return savedChargeArea;

  const savedPlotArea = Number(savedDemandNote.area);
  const editedPlotArea = Number(application?.area_of_plot);
  const isInSquareFeet = String(application?.unit_area_of_plot || "")
    .toLowerCase()
    .includes("ft");

  if (!savedPlotArea || !editedPlotArea || isInSquareFeet) return savedChargeArea;
  return savedPlotArea === editedPlotArea ? savedChargeArea : editedPlotArea;
}
